import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'semantic-ui-react';
import css from './TrackingRuleRow.scss';
import classnames from 'classnames';
import Icon from 'SF/MaterialIcon';
import { getWord } from 'utils/tenant';
import { pressedEnterOrSpace } from 'utils/accessibility';
import InterventionIcon from 'common/InterventionIcon';

const TrackingRuleRow = ({ trackingRule }) => {
    const [showExpandedRow, setShowExpandedRow] = useState(false);
    
    const getToggleIcon = () => showExpandedRow ? 'arrow_drop_up_circle' : 'arrow_drop_down_circle';
    
    const getCategory = () => {
        if (trackingRule.category === 'KUDO') return getWord('kudos');
        if (trackingRule.category === 'TO_DO') return 'To-Do';
        if (trackingRule.category === 'REFERRAL') return 'Referral';
        
        return 'Flag: ' + 
            trackingRule.subcategory.toLowerCase()
                .split('_')
                .map(word => word[0].toUpperCase() + word.slice(1))
                .join(' ');
    };
    
    const getInterventionIcon = () => {
        const category = trackingRule.category === 'KUDO' ? 'kudos'  // weird consequence of legacy backend spelling
            : trackingRule.category.toLowerCase().replace('_', '-');
        return <InterventionIcon category={category}
                                 className={classnames(css['intervention-icon'], css[category])}
                                 aria-hidden={true}
                                 aria-label={null} />
    };
    
    const getToggleButton = () => (
        <div className={css['toggle-row-button']}
             onClick={() => setShowExpandedRow(!showExpandedRow)}
             onKeyDown={(e) => {
                 if (pressedEnterOrSpace(e)) { 
                     e.preventDefault();
                     setShowExpandedRow(!showExpandedRow);
                 }
             }}
             aria-label={`${trackingRule.name} Details`}
             role="button"
             aria-expanded={showExpandedRow}
             tabIndex="0">
            <Icon icon={getToggleIcon()} aria-hidden={true} />
        </div>
    );

    /**
     * @param {boolean} expanded - is the row and expanded row or not
     * @returns {string} - Returns a testId based on the tracking rule's name.
     * (E.g., rule with name "Academic Flag" will have a testId of ruleRowAcademicFlag.)
     */
    const getTestId = (expanded) => {
        const testId = `ruleRow${trackingRule.name.replace(/\s+/g, '')}`;
        return expanded ? testId + 'Expanded' : testId;
    };
    
    return (
        <>
            <Table.Row className={classnames(css['item-row'], {[css.expanded]: showExpandedRow})}
                       data-testid={getTestId(false)}
                       onClick={() => setShowExpandedRow(!showExpandedRow)}>
                <Table.Cell className={classnames({[css.emergency]: trackingRule.emergency})}>
                    <div className={css['row-contents']}>
                        { trackingRule.emergency && <Icon icon='error' className={css['emergency-icon']} aria-label="Emergency item" /> }
                        { getInterventionIcon() }
                        { trackingRule.name }
                    </div>
                    { getToggleButton() }
                </Table.Cell>
            </Table.Row>
            { showExpandedRow &&
                <Table.Row className={css['expanded-row']} data-testid={getTestId(true)}>
                    <Table.Cell>
                        <span className={css['category']}>{getCategory()}</span>
                        <br/>
                        <span className={css['description']}>{trackingRule.description}</span>
                    </Table.Cell>
                </Table.Row>
            }
        </>
    )
};

TrackingRuleRow.propTypes = {
    trackingRule: PropTypes.shape({
        category: PropTypes.oneOf(['FLAG', 'KUDO', 'TO_DO', 'REFERRAL']),
        description: PropTypes.string,
        emergency: PropTypes.bool,
        name: PropTypes.string,
        subcategory: PropTypes.string
    })
};

export default TrackingRuleRow;
